import React from 'react';
import { SectionHeader } from '../components';
import { Copy, RefreshCw } from 'lucide-react';

export function SettingsPage({ dashboard, patch, regenerate }) {
  const isDemo = dashboard?.mode !== 'live';
  const sdkSnippet =
`// Drop this once in your app to start streaming events to your cockpit
import { BTBA } from '@btba/sdk';

BTBA.init({
  dashboard_id: '${dashboard?.dashboard_id || ''}',
  app_url: window.location.origin,
});

// Then track anywhere in your app
BTBA.track('order.created', { amount: 99, customer_id: 'usr_42' });`;

  return (
    <div>
      <SectionHeader title="Settings" subtitle="Mode · Branding · SDK" />

      {/* Mode card */}
      <SettingsCard title="Cockpit mode">
        <p style={{ fontSize: 13, color: 'var(--dash-text-muted)', margin: '0 0 14px' }}>
          Switch to <strong>Production</strong> once your app is streaming
          events via the BTBA SDK. <strong>Preview</strong> shows sample data
          Aria crafted from your project's DNA so you can see the cockpit
          fully populated before going live.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <ModeButton
            label="Preview"
            sub="Sample data crafted by Aria"
            active={isDemo}
            onClick={() => patch?.({ mode: 'demo' })}
            testid="mode-demo"
          />
          <ModeButton
            label="Production"
            sub="Real events from your project"
            active={!isDemo}
            onClick={() => patch?.({ mode: 'live' })}
            testid="mode-live"
          />
        </div>
      </SettingsCard>

      {/* SDK card */}
      <SettingsCard title="BTBA SDK">
        <p style={{ fontSize: 13, color: 'var(--dash-text-muted)', margin: '0 0 12px' }}>
          Copy this snippet into your app to start streaming real events.
          Available in JavaScript, Python, and as a REST endpoint.
        </p>
        <pre style={{
          margin: 0, padding: 14,
          background: '#0F0F1E', color: '#F4F3F0',
          borderRadius: 10, fontSize: 12,
          fontFamily: "'JetBrains Mono', monospace",
          overflow: 'auto', position: 'relative',
        }}>
          <button
            data-testid="copy-sdk-btn"
            onClick={() => navigator.clipboard?.writeText(sdkSnippet)}
            style={{
              position: 'absolute', top: 8, right: 8,
              padding: '4px 8px', background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 6,
              color: '#F4F3F0', fontSize: 11, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontFamily: 'inherit',
            }}
          >
            <Copy size={11} /> Copy
          </button>
          {sdkSnippet}
        </pre>
      </SettingsCard>

      {/* Detected DNA */}
      <SettingsCard title="Project DNA">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13 }}>
          <DnaRow label="Vertical"   value={dashboard?.dna?.vertical} />
          <DnaRow label="Confidence" value={`${Math.round(((dashboard?.dna?.vertical_confidence) || 0) * 100)}%`} />
          <DnaRow label="Primary"
                  value={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        display: 'inline-block', width: 14, height: 14, borderRadius: 4,
                        background: dashboard?.theme?.primary, border: '1px solid rgba(15,15,30,0.10)',
                      }} />
                      {dashboard?.theme?.primary}
                    </span>}
          />
          <DnaRow label="Font" value={dashboard?.theme?.font_display?.split(',')[0]} />
        </div>
        <button
          data-testid="regenerate-data-btn"
          onClick={regenerate}
          style={{
            marginTop: 18,
            padding: '8px 14px', background: 'var(--dash-primary)',
            color: 'var(--dash-primary-text)', border: 'none', borderRadius: 8,
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: 'inherit',
          }}
        >
          <RefreshCw size={12} /> Re-scan project DNA
        </button>
      </SettingsCard>
    </div>
  );
}

function SettingsCard({ title, children }) {
  return (
    <div style={{
      padding: 22, marginBottom: 16,
      background: 'var(--dash-bg-card)',
      border: '1px solid var(--dash-border)',
      borderRadius: 'var(--dash-radius)',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
        textTransform: 'uppercase', color: 'var(--dash-text-muted)',
        marginBottom: 14,
      }}>{title}</div>
      {children}
    </div>
  );
}

function DnaRow({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.10em',
        textTransform: 'uppercase', color: 'var(--dash-text-muted)' }}>{label}</span>
      <span style={{ fontSize: 14, color: 'var(--dash-text)', textTransform: 'capitalize' }}>
        {value || '—'}
      </span>
    </div>
  );
}

function ModeButton({ label, sub, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        flex: 1, padding: '14px 18px',
        background: active ? 'var(--dash-primary)' : 'rgba(15,15,30,0.03)',
        color: active ? 'var(--dash-primary-text)' : 'var(--dash-text)',
        border: active ? 'none' : '1px solid var(--dash-border)',
        borderRadius: 10,
        cursor: 'pointer', fontFamily: 'inherit',
        textAlign: 'left',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 11, opacity: 0.75 }}>{sub}</div>
    </button>
  );
}
